import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { 
  LockOpen as LockOpenIcon, 
  AdminPanelSettings as AdminIcon,
  CheckCircle as CheckIcon,
  Logout as LogoutIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Logo } from '@horwath-htl/shared-branding';

export const AuthorizationNeededPage: React.FC = () => {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  
  // Get tenant ID from the authenticated user
  const tenantId = accounts[0]?.tenantId;
  const clientId = (window as any).ENV?.VITE_AZURE_CLIENT_ID || import.meta.env.VITE_AZURE_CLIENT_ID || '';
  
  // Construct the admin consent URL
  const adminConsentUrl = tenantId && clientId
    ? `https://login.microsoftonline.com/${tenantId}/adminconsent?client_id=${clientId}`
    : `https://login.microsoftonline.com/{tenant}/adminconsent?client_id=${clientId || '{client_id}'}`;

  const handleLogout = async () => {
    try {
      // Log out the user
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin, // Redirect to root after logout
      });
    } catch (error) {
      console.error('Logout failed:', error);
      // Fallback: navigate to root anyway
      navigate('/');
    }
  };
  
  const handleCopyUrl = () => {
    navigator.clipboard.writeText(adminConsentUrl).then(() => {
      // Could add a snackbar notification here if desired
      console.log('Admin consent URL copied to clipboard');
    }).catch((error) => {
      console.error('Failed to copy URL:', error);
    });
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card sx={{ textAlign: 'center', p: 4 }}>
        <CardContent>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Logo variant="full" height={50} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <LockOpenIcon sx={{ fontSize: 64, color: 'warning.main' }} />
            </Box>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              fontWeight="bold"
            >
              Authorization Needed
            </Typography>
            <Typography variant="h6" component="h2" gutterBottom color="text.secondary">
              Additional permissions required
            </Typography>
          </Box>

          <Paper sx={{ p: 3, mb: 4, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.main', textAlign: 'left' }}>
            <Typography variant="body1" gutterBottom fontWeight="medium">
              To access this portal, you need to contact your Active Directory administrator 
              to have the Barrett HTL AAD application approved.
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
              Please follow these steps:
            </Typography>
            
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Contact your IT administrator or Active Directory administrator"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Request approval for the 'Barrett HTL AAD' application"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Once approved, you'll be able to access this portal"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>
          </Paper>

          <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.main' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              For Your Administrator
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Share this URL with your IT administrator to approve the Barrett HTL AAD application:
            </Typography>
            <Paper 
              sx={{ 
                p: 2, 
                bgcolor: 'grey.50', 
                border: '1px solid', 
                borderColor: 'grey.300',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                wordBreak: 'break-all',
                mb: 2
              }}
            >
              {adminConsentUrl}
            </Paper>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyUrl}
              sx={{ textTransform: 'none' }}
            >
              Copy URL
            </Button>
            {!tenantId && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                Note: Replace {'{tenant}'} with your organization's tenant ID or domain name
              </Typography>
            )}
          </Paper>

          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
              }}
            >
              Sign Out
            </Button>
          </Box>

          <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <AdminIcon color="action" fontSize="small" />
              <Typography variant="caption" color="text.secondary">
                For administrator assistance, contact your organization's IT support team
              </Typography>
            </Box>
          </Paper>

          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              If you continue to experience issues, please contact support@chathtl.com
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};